import Vue from 'vue';
import VueRouter from 'vue-router';
import VueAnalytics from 'vue-analytics';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';

import HomeView from '../views/HomeView.vue';
import TutorialView from '../views/TutorialView.vue';
import PatternView from '../views/PatternView.vue';
import NotFoundView from '../views/NotFoundView.vue';
import LoginView from '../views/LoginView.vue';
import EmailCollectView from '../views/EmailCollectView.vue';
import HelpView from '../views/HelpView.vue';
import PlaygroundView from '../views/PlaygroundView.vue';
import SummaryView from '../views/SummaryView.vue';

import Db from '@/helpers/Db';
import HelperMethods from '@/helpers/HelperMethods';
import { ENV } from '@/environment';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
  },
  {
    path: '/tutorial/:id',
    name: 'tutorial',
    component: TutorialView,
  },
  {
    path: '/tutorial/:id/pattern',
    name: 'pattern',
    component: PatternView,
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
  },
  {
    path: '/email',
    name: 'email',
    component: EmailCollectView,
  },
  {
    path: '/help/:parentId?/:childId?/:microstepId?',
    name: 'help',
    component: HelpView,
  },
  {
    path: '/tutorial/:id/summary',
    name: 'summary',
    component: SummaryView,
  },
  {
    path: '/playground',
    name: 'playground',
    component: PlaygroundView,
  },
  {
    name: 'notfound',
    path: '*',
    component: NotFoundView,
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
});

if (!ENV.DEV) {
  Sentry.init({
    Vue,
    dsn: 'https://a52aa75a694f4b1e96c78c9bbd6d41e7@o4504160020463616.ingest.sentry.io/4504160541540352',
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
        tracingOrigins: [
          'http://woobles-staging-preview.s3-website-us-east-1.amazonaws.com/',
          'learn.thewoobles.com',
          /^\//,
        ],
      }),
    ],
    ignoreErrors: [
      'Navigation cancelled',
      'save power',
      'The play() request was interrupted',
      'interact with the document first',
      'user denied permission',
    ],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  });
}

//and then use it in main.js
Vue.use(VueAnalytics, {
  id: 'UA-230546080-1',
  router,
});

router.beforeEach(async (to, from, next) => {
  // At Home just proceed
  if (to.name === 'home') {
    next();
    return;
  }

  const jwtObject = HelperMethods.getWooJwt();

  // If we're not logged in, redirect to login page with return path encoded
  if (Object.keys(jwtObject).length === 0 && !['login', 'email'].some(t => t === to.name)) {
    next(`/login?r=${encodeURIComponent(to.fullPath)}`);
    return;
  }

  // Override to display the step name as a title
  if (['tutorial', 'pattern'].includes(to.name) && to.name !== 'login') {
    // We're not logged in for this tutorial
    const loggedIn = await HelperMethods.isLoggedIn({ slug: to.params.id });
    if (!loggedIn) {
      next(`/login?r=${encodeURIComponent(to.fullPath)}`);
      return;
    }

    let tutorial = Db.getTutorialBySlug(to.params.id);

    if (tutorial) {
      document.title = tutorial.display_name;
    }

    if (tutorial?.redirect_url) {
      window.location = tutorial.redirect_url;
      return;
    }

    // If we get here, it means we're logged in. If we don't have
    // any passcodes without a redirect, it means we need to redirect
    const storedPasscodes = jwtObject[to.params.id];
    if (!storedPasscodes.some(p => !p.redirect_url)) {
      const passcodeWithRedirect = storedPasscodes.find(p => p.redirect_url);
      if (passcodeWithRedirect) {
        const { redirect_url } = passcodeWithRedirect;
        window.location = redirect_url;
        return;
      }
    }
  }

  if (to.name === 'summary') {
    document.title = 'Summary';
  }

  // This goes through the matched routes from last to first, finding the closest route with a title.
  // e.g., if we have `/some/deep/nested/route` and `/some`, `/deep`, and `/nested` have titles,
  // `/nested`'s will be chosen.
  const nearestWithTitle = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.title);

  // Find the nearest route element with meta tags.
  const nearestWithMeta = to.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.metaTags);

  const previousNearestWithMeta = from.matched
    .slice()
    .reverse()
    .find(r => r.meta && r.meta.metaTags);

  // If a route with a title was found, set the document (page) title to that value.
  if (nearestWithTitle) {
    document.title = nearestWithTitle.meta.title;
  } else if (previousNearestWithMeta) {
    document.title = previousNearestWithMeta.meta.title;
  }

  // Remove any stale meta tags from the document using the key attribute we set below.
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(el => el.parentNode.removeChild(el));

  // Skip rendering meta tags if there are none.
  if (!nearestWithMeta) return next();

  // Turn the meta tag definitions into actual elements in the head.
  nearestWithMeta.meta.metaTags
    .map(tagDef => {
      const tag = document.createElement('meta');

      Object.keys(tagDef).forEach(key => {
        tag.setAttribute(key, tagDef[key]);
      });

      // We use this to track which meta tags we create so we don't interfere with other ones.
      tag.setAttribute('data-vue-router-controlled', '');

      return tag;
    })
    // Add the meta tags to the document head.
    .forEach(tag => document.head.appendChild(tag));

  next();
});

export default router;
